import gsap from "gsap";
import "../sticker.css";
import { navigate } from "../router";
import { store, type ProfanityLevel } from "../state";
import { openLookSheet } from "../look";
import { PROFANITY_STEPS, getReachedHeadline, getReachedPunchline } from "../copy";
import { censorText, getDailyCopy, getDailyHeadline, localDayKey } from "../copy/engine";
import { shareCountdownCard } from "../share";
import { dialCellsHTML, mountDial } from "../ui/dial";
import { createFitter, cssPx } from "../ui/fit";
import { countUp, playEntrance, setCount, takeFirstRun } from "../ui/entrance";
import { createTimeLeft, everySecond, pulse } from "./clock";
import { bellHTML, mountBell } from "./reminderBell";

/**
 * "Sticker" countdown — the approved Paper artefact
 * (claude.ai/artifact/VuWesDjty9VKCWhfERMx5e) ported as-is: same markup,
 * same tokens (sticker.css), same motion. The only differences are the
 * things a real app needs that a mock doesn't: live event data and daily
 * copy from the store, a working share button, and the gear opening the
 * look sheet (themes, colour mode, and a way through to all settings).
 */

export const SHARE_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M7 17L17 7"/><path d="M9 7h8v8"/></svg>`;
export const GEAR_SVG = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>`;

export const SUB = "As of this current moment there are exactly, precisely and unavoidably:";
/** The artefact's hero size; denser fits lower it via --hero-max in sticker.css. */
const HERO_MAX_PX = 92;

export const fmt = (n: number) => n.toLocaleString("en-GB");

export function formatEventDate(iso: string): string {
  return new Date(iso)
    .toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
    .toUpperCase();
}

/** Weekdays (Mon-Fri) remaining between now and target. */
export function countWorkdays(now: Date, target: Date): number {
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);
  const end = new Date(target);
  end.setHours(0, 0, 0, 0);
  const totalDays = Math.max(0, Math.round((end.getTime() - start.getTime()) / 86400000));
  let count = Math.floor(totalDays / 7) * 5;
  let dow = (start.getDay() + 1) % 7;
  for (let i = 0; i < totalDays % 7; i++) {
    if (dow !== 0 && dow !== 6) count++;
    dow = (dow + 1) % 7;
  }
  return count;
}

export function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!);
}

export function renderCountdownSticker(root: HTMLElement): () => void {
  const event = store.getPrimaryEvent();
  if (!event) {
    navigate("#/edit", { kind: "none" });
    return () => {};
  }

  root.innerHTML = `
    <div class="stk" data-el="screen">
      <div class="stk-page">
        <div class="stk-shape stk-sparkle"></div>
        <div class="stk-shape stk-ring-blue"></div>
        <div class="stk-shape stk-ring-pink"></div>
        <div class="stk-shape stk-daisy">
          ${[0, 45, 90, 135, 180, 225, 270, 315]
            .map((a) => `<i style="transform:translate(-50%,-50%) rotate(${a}deg) translateY(-14px)"></i>`)
            .join("")}
        </div>

        <div class="stk-content">
          <div class="stk-topbar">
            <div class="stk-wordmark">
              <span class="stk-logo"><span class="stk-logo-spark" data-el="logoSpark"></span></span>
              <span>C<span class="stk-censor">*</span>UNTDOWN</span>
            </div>
            <div class="stk-eyes" data-el="eyes" aria-hidden="true">
              <div class="stk-eye"><div class="stk-pupil"></div></div>
              <div class="stk-eye"><div class="stk-pupil"></div></div>
            </div>
            <div class="stk-icons">
              <button type="button" class="stk-icon-btn" data-el="shareBtn" aria-label="Share this countdown">${SHARE_SVG}</button>
              <button type="button" class="stk-icon-btn stk-lime" data-el="settingsBtn" aria-label="Look and settings" aria-haspopup="dialog">${GEAR_SVG}</button>
            </div>
          </div>
          <div class="stk-rule"></div>

          <div class="stk-ticket">
            <span class="stk-ticket-name">${escapeHtml(event.name || "The reckoning")}</span>
            <span class="cd-ticket-end">${bellHTML("stk-bell")}<span class="stk-ticket-date">${formatEventDate(event.targetDate)}</span></span>
          </div>

          <div class="stk-headline" data-el="headline"></div>
          <div class="stk-sub">${SUB}</div>

          <div class="stk-units">
            <div class="stk-hero" data-el="hero">
              <span class="stk-hero-tag">days</span>
              <span class="stk-hero-num" data-el="numDays"></span>
            </div>
            <div class="stk-chip stk-chip-workdays">
              <span class="stk-chip-num" data-el="numWorkdays"></span>
              <span class="stk-chip-label">work days</span>
            </div>
            <div class="stk-chip stk-chip-hours">
              <span class="stk-chip-num" data-el="numHours"></span>
              <span class="stk-chip-label">hours</span>
            </div>
            <div class="stk-seconds"><span class="stk-seconds-lead">for reference, </span>that's exactly <b data-el="numSeconds"></b> seconds</div>
          </div>

          <div class="stk-punchline" data-el="punchline">
            <div class="stk-punchline-main" data-el="punchlineMain"></div>
            <div class="stk-punchline-sub" data-el="punchlineSub"></div>
          </div>

          <div class="stk-bottom">
            <div class="stk-ruler-block">
              <div class="stk-caption-row"><span data-el="pctLabel"></span><span class="stk-accent" data-el="leftLabel"></span></div>
              <div class="stk-ruler"><div class="stk-ruler-fill" data-el="rulerFill"></div></div>
            </div>
            <div class="stk-dial-row cd-dial" data-el="dialRow">${dialCellsHTML("stk-dial-cell")}</div>
          </div>
        </div>

        <div class="stk-over stk-flash"></div>
        <div class="stk-over stk-star-sm"></div>
      </div>
    </div>
  `;

  const el = <T extends HTMLElement = HTMLElement>(name: string) => root.querySelector<T>(`[data-el="${name}"]`)!;

  const screen = el("screen");
  const headline = el("headline");
  const hero = el("hero");
  const numDays = el("numDays");
  const numWorkdays = el("numWorkdays");
  const numHours = el("numHours");
  const numSeconds = el("numSeconds");
  const punchline = el("punchline");
  const punchlineMain = el("punchlineMain");
  const punchlineSub = el("punchlineSub");
  const pctLabel = el("pctLabel");
  const leftLabel = el("leftLabel");
  const rulerFill = el("rulerFill");
  const logoSpark = el("logoSpark");
  const shareBtn = el<HTMLButtonElement>("shareBtn");
  const settingsBtn = el<HTMLButtonElement>("settingsBtn");

  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // ---------- Look: the store is the one source of truth, driven from the
  // gear's look sheet, the bottom dot toggle, or Settings. ----------
  settingsBtn.addEventListener("click", () => openLookSheet(settingsBtn));

  // ---------- Profanity dial + copy ----------
  // The dial is a sliding sticker; each change slaps the punchline card back
  // down, sets the eyes' mood (bored, fine, wide) and throws stickers out of
  // the picked cell, more of them the ruder it gets.
  const page = root.querySelector<HTMLElement>(".stk-page")!;
  const eyes = el("eyes");
  const BURST_COLOURS = ["var(--lime)", "var(--sky)", "var(--pink)", "var(--accent)", "var(--yellow)"];
  const BURST_SHAPES = ["star", "spark", "ring", "dot"];

  function burst(from: HTMLElement, count: number) {
    const p = page.getBoundingClientRect();
    const c = from.getBoundingClientRect();
    const x0 = c.left - p.left + c.width / 2;
    const y0 = c.top - p.top + c.height / 2;
    for (let i = 0; i < count; i++) {
      const bit = document.createElement("span");
      bit.className = `stk-bit stk-bit-${BURST_SHAPES[i % BURST_SHAPES.length]}`;
      bit.style.setProperty("--c", BURST_COLOURS[(i * 3 + count) % BURST_COLOURS.length]);
      bit.style.left = `${x0}px`;
      bit.style.top = `${y0}px`;
      page.appendChild(bit);
      const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 1.1;
      const dist = 70 + Math.random() * (60 + count * 9);
      gsap
        .timeline({ onComplete: () => bit.remove() })
        .fromTo(bit, { x: 0, y: 0, scale: 0, rotation: 0 }, {
          x: Math.cos(angle) * dist,
          y: Math.sin(angle) * dist,
          scale: 0.7 + Math.random() * 0.6,
          rotation: (Math.random() - 0.5) * 540,
          duration: 0.55 + Math.random() * 0.2,
          ease: "power3.out",
        })
        .to(bit, { y: "+=40", scale: 0, duration: 0.35, ease: "power2.in" }, "-=0.08");
    }
  }

  const dial = mountDial(el("dialRow"), (level, prev, cell) => {
    if (reducedMotion) return;
    gsap.fromTo(
      punchline,
      { rotation: level > prev ? -2.4 : 2.4, y: -10, scale: 1.035 },
      { rotation: 0, y: 0, scale: 1, duration: 0.6, ease: "back.out(2.6)", overwrite: true }
    );
    gsap.fromTo(eyes, { scaleY: 0.12 }, { scaleY: 1, duration: 0.32, ease: "power2.out", delay: 0.05 });
    burst(cell, [3, 6, 12][level]);
    if (level === 2) gsap.fromTo(page, { x: -6 }, { x: 0, duration: 0.5, ease: "elastic.out(1.4, 0.2)", clearProps: "transform" });
  });
  let wasReached = false;

  const copyContext = (level: ProfanityLevel) => ({
    level,
    eventId: event!.id,
    eventName: event!.name,
    personName: event!.personName,
    target: event!.targetDate,
  });

  function applyCopy(level: ProfanityLevel) {
    const step = PROFANITY_STEPS[level];
    punchline.dataset.level = step.label;
    screen.dataset.level = String(level);
    dial.sync(level);
    const censorOn = store.get().censorEnabled;
    if (wasReached) {
      headline.textContent = getReachedHeadline(event!.personName, level);
      punchlineMain.textContent = censorText(getReachedPunchline(event!.name, level), level, censorOn);
      punchlineSub.textContent = "";
    } else {
      headline.textContent = getDailyHeadline(copyContext(level));
      const copy = getDailyCopy(copyContext(level));
      // Lower-case "of": it reads on from the numbers above.
      punchlineMain.textContent = `of ${censorText(copy.main, level, censorOn)}`;
      punchlineSub.textContent = censorText(copy.sub, level, censorOn);
    }
  }

  // Shrink the hero numeral only when it would overflow its card (e.g. a
  // four-digit day count); otherwise it stays at the artefact's 92px.
  let lastDaysLen = -1;
  function fitHero() {
    const max = cssPx(screen, "--hero-max", HERO_MAX_PX);
    numDays.style.fontSize = `${max}px`;
    const avail = hero.clientWidth - 36;
    const w = numDays.scrollWidth;
    if (w > avail) numDays.style.fontSize = `${Math.max(40, Math.floor((max * avail) / w))}px`;
  }
  const fitter = createFitter(screen, screen, fitHero);

  // ---------- Clock ----------
  let sparkRot = 0;
  let lastDayKey = localDayKey(new Date());

  const timeLeft = createTimeLeft(leftLabel, fmt, reducedMotion);

  function tick() {
    const now = Date.now();
    const target = new Date(event!.targetDate).getTime();
    const start = new Date(event!.startDate).getTime();
    const remaining = Math.max(0, target - now);
    const reached = remaining <= 0;
    const days = Math.floor(remaining / 86400000);
    const hours = Math.floor(remaining / 3600000);
    const seconds = Math.floor(remaining / 1000);

    const daysText = fmt(days);
    setCount(numDays, daysText);
    setCount(numWorkdays, fmt(reached ? 0 : countWorkdays(new Date(now), new Date(target))));
    setCount(numHours, fmt(hours));
    numSeconds.textContent = fmt(seconds);
    pulse(numSeconds, reducedMotion, now);

    if (daysText.length !== lastDaysLen) {
      lastDaysLen = daysText.length;
      fitHero();
      fitter.refit(false);
    }

    const dayKey = localDayKey(new Date(now));
    if (reached !== wasReached || dayKey !== lastDayKey) {
      wasReached = reached;
      lastDayKey = dayKey;
      applyCopy(store.get().profanityLevel);
    }

    const span = Math.max(1, target - start);
    const elapsed = Math.min(Math.max(now - start, 0), span);
    const pct = Math.min(100, Math.max(0, (elapsed / span) * 100));
    pctLabel.textContent = `${Math.round(pct)}% done`;
    timeLeft.update(now, target);
    rulerFill.style.width = `${pct.toFixed(1)}%`;

    // Only the logo's own sparkle snaps with the clock.
    if (!reducedMotion) {
      sparkRot += 90;
      gsap.to(logoSpark, { rotation: sparkRot, duration: 0.55, ease: "back.out(2.4)" });
    }
  }

  // ---------- Ambient GSAP motion (replaces the CSS keyframes) ----------
  const tweens: (gsap.core.Timeline | gsap.core.Tween)[] = [];
  const delayed: gsap.core.Tween[] = [];
  if (!reducedMotion) {
    const rand = (min: number, max: number) => min + Math.random() * (max - min);
    const wander = (
      selector: string,
      opts: { x: [number, number]; y: [number, number]; rot: [number, number]; dur: [number, number]; ease?: string }
    ) => {
      const target = root.querySelector<HTMLElement>(selector);
      if (!target) return;
      target.style.animation = "none";
      const tl = gsap.timeline({ repeat: -1, paused: true, defaults: { ease: opts.ease ?? "sine.inOut" } });
      for (let i = 0; i < 3; i++) {
        tl.to(target, { x: rand(...opts.x), y: rand(...opts.y), rotation: rand(...opts.rot), duration: rand(...opts.dur) });
      }
      tl.to(target, { x: 0, y: 0, rotation: opts.rot[0], duration: rand(...opts.dur) });
      delayed.push(gsap.delayedCall(rand(0, 1.2), () => void tl.play()));
      tweens.push(tl);
    };

    root.querySelectorAll<HTMLElement>(".stk-sparkle, .stk-daisy").forEach((target, i) => {
      target.style.animation = "none";
      tweens.push(gsap.to(target, { rotation: i % 2 ? -360 : 360, duration: 20 + i * 6, ease: "none", repeat: -1 }));
    });

    wander(".stk-ring-blue", { x: [-14, 18], y: [-26, 6], rot: [-16, 14], dur: [2.8, 4.2] });
    wander(".stk-ring-pink", { x: [-12, 16], y: [-20, 8], rot: [-14, 20], dur: [3, 4.4] });
    wander(".stk-flash", { x: [-18, 14], y: [-22, 16], rot: [-20, 20], dur: [2, 3], ease: "back.inOut(1.6)" });
    wander(".stk-star-sm", { x: [-16, 18], y: [-18, 20], rot: [-26, 26], dur: [1.8, 2.8], ease: "back.inOut(1.6)" });

    eyes.style.animation = "none";
    tweens.push(gsap.to(eyes, { x: 5, y: -6, rotation: 4, duration: 2.6, ease: "sine.inOut", yoyo: true, repeat: -1 }));
  }

  // ---------- Share ----------
  shareBtn.addEventListener("click", async () => {
    if (shareBtn.classList.contains("busy")) return;
    shareBtn.classList.add("busy");
    shareBtn.classList.remove("share-ok", "share-fail");
    const result = await shareCountdownCard({
      eventName: event!.name || "The reckoning",
      eventDate: formatEventDate(event!.targetDate),
      daysLeft: Math.floor(Math.max(0, new Date(event!.targetDate).getTime() - Date.now()) / 86400000),
      punchlineMain: punchlineMain.textContent ?? "",
      punchlineSimile: punchlineSub.textContent ?? "",
    });
    shareBtn.classList.remove("busy");
    shareBtn.classList.add(result === "failed" ? "share-fail" : "share-ok");
    window.setTimeout(() => shareBtn.classList.remove("share-ok", "share-fail"), 1600);
  });

  tick();
  applyCopy(store.get().profanityLevel);
  fitter.refit();

  // ---------- Entrance: stickers pressed on one by one, numbers counting
  // up; the first open after onboarding is slower and ends in a burst. ----------
  const firstRun = takeFirstRun();
  {
    const q = (sel: string) => root.querySelector<HTMLElement>(sel);
    playEntrance(
      [q(".stk-topbar"), q(".stk-ticket"), headline, q(".stk-sub"), hero, q(".stk-chip-workdays"), q(".stk-chip-hours"), q(".stk-seconds"), punchline, q(".stk-bottom")],
      "stick",
      firstRun
    );
    const target = new Date(event.targetDate).getTime();
    const remaining = Math.max(0, target - Date.now());
    const ms = firstRun ? 1500 : 650;
    countUp(numDays, Math.floor(remaining / 86400000), fmt, ms, fitHero);
    countUp(numWorkdays, countWorkdays(new Date(), new Date(target)), fmt, ms);
    countUp(numHours, Math.floor(remaining / 3600000), fmt, ms);
    if (firstRun && !reducedMotion) {
      window.setTimeout(() => {
        if (!hero.isConnected) return;
        burst(hero, 14);
        gsap.fromTo(hero, { scale: 1.06, rotation: -2 }, { scale: 1, rotation: 0, duration: 0.7, ease: "elastic.out(1.1, 0.35)" });
      }, 1500);
    }
  }
  const stopClock = everySecond(tick);
  const unmountBell = mountBell(root);

  const unsubscribe = store.subscribe((state) => {
    applyCopy(state.profanityLevel);
    fitter.refit(false);
  });


  return () => {
    stopClock();
    unmountBell();
    tweens.forEach((t) => t.kill());
    delayed.forEach((t) => t.kill());
    gsap.killTweensOf(logoSpark);
    unsubscribe();
    dial.destroy();
    gsap.killTweensOf([punchline, eyes, page, hero]);
    fitter.destroy();
  };
}
